import * as THREE from "three";

/**
 * A tier-IV tower before its own model ships (M21, docs/TOWERS_V2.md "Tier 4"): the tier-3 model dressed in gold — a
 * heavy gold ring at its foot, a thin crown ring turning over its top, and its lit materials warmed toward gold — so a
 * tier-IV tower reads at a glance on the map. When `tower_<tower>_t4_<branch>` is in the manifest the model itself is
 * used instead (views/common.ts hqTowerName) and none of this is added.
 */

const GOLD = 0xffc94a;
const GOLD_DEEP = 0xc8901e;
/** How far the lit materials move toward gold, and the gold glow they gain (subtle: the tower must stay itself). */
const TINT = 0.15;
const GLOW = 0.06;
const FOOT_RADIUS = 0.47, FOOT_TUBE = 0.04;
const CROWN_RADIUS = 0.2, CROWN_TUBE = 0.018, CROWN_LIFT = 0.18;
/** The crown ring's turn, radians per second. */
const CROWN_SPIN = 0.9;

const footGeo = new THREE.TorusGeometry(FOOT_RADIUS, FOOT_TUBE, 10, 48);
const crownGeo = new THREE.TorusGeometry(CROWN_RADIUS, CROWN_TUBE, 8, 32);
let goldMat: THREE.MeshStandardMaterial | null = null;
let glowMat: THREE.MeshBasicMaterial | null = null;
const gold = (): THREE.MeshStandardMaterial => (goldMat ??= new THREE.MeshStandardMaterial({ color: GOLD, metalness: 0.85, roughness: 0.28, emissive: GOLD_DEEP, emissiveIntensity: 0.55 }));
const glow = (): THREE.MeshBasicMaterial => (glowMat ??= new THREE.MeshBasicMaterial({ color: GOLD, transparent: true, opacity: 0.85, depthWrite: false, blending: THREE.AdditiveBlending, toneMapped: false }));

/** The name of the accent group (so a test or a capture can find it). */
export const TIER4_ACCENT = "tier4Accent";

/** Dresses a tier-3 model as tier IV: `height` is the model's height (cells), for the crown ring. */
export function dressTierFour(model: THREE.Object3D, height: number): void {
  warmMaterials(model);
  const g = new THREE.Group();
  g.name = TIER4_ACCENT;
  const foot = new THREE.Mesh(footGeo, gold());
  foot.rotation.x = Math.PI / 2;
  foot.position.y = FOOT_TUBE;
  foot.castShadow = true;
  g.add(foot);
  const crown = new THREE.Mesh(crownGeo, glow());
  crown.rotation.x = Math.PI / 2;
  crown.position.y = height + CROWN_LIFT;
  const start = performance.now();
  crown.onBeforeRender = () => { crown.rotation.z = ((performance.now() - start) / 1000) * CROWN_SPIN; crown.position.y = height + CROWN_LIFT + Math.sin((performance.now() - start) / 600) * 0.03; };
  g.add(crown);
  model.add(g);
}

/**
 * Each lit (standard) material on the model, copied and warmed toward gold. The copies keep the shared shader hooks
 * (wind, the look's extras) by carrying the original's `onBeforeCompile` and program key; glow slots (basic materials)
 * are left alone.
 */
function warmMaterials(model: THREE.Object3D): void {
  const warm = new Map<THREE.Material, THREE.Material>();
  const target = new THREE.Color(GOLD);
  model.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    const swap = (m: THREE.Material): THREE.Material => {
      if (!(m instanceof THREE.MeshStandardMaterial)) return m;
      let w = warm.get(m);
      if (!w) {
        const c = m.clone();
        c.onBeforeCompile = m.onBeforeCompile;
        c.customProgramCacheKey = m.customProgramCacheKey;
        c.color.lerp(target, TINT);
        c.emissive.lerp(target, GLOW);
        if (c.emissiveIntensity < 1) c.emissiveIntensity = 1;
        w = c;
        warm.set(m, w);
      }
      return w;
    };
    mesh.material = Array.isArray(mesh.material) ? mesh.material.map(swap) : swap(mesh.material);
  });
}
